<template>
  <div class="volumetria">
    <div class="row mt-4 align-items-end">
      <div class="col-auto">
        <div class="btn-group">
          <button class="btn btn-sm" :class="view === 'realizado' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setView('realizado')">Realizado</button>
          <button class="btn btn-sm" :class="view === 'previsto' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setView('previsto')">Previsto</button>
        </div>
      </div>

      <div class="col-md-3">
        <label>Spider</label>
        <select v-model="spider" class="form-control" @change="fetchData()">
          <option value="">Todas as spiders</option>
          <option v-if="sneNames.length" value="__sne__">SNE (todas as modalidades)</option>
          <option v-for="s in services" :key="s.name" :value="s.name">
            {{ s.friendly_name || s.name }}
          </option>
        </select>
      </div>

      <div class="col-md-2">
        <label>App</label>
        <select v-model="app" class="form-control" @change="fetchData()">
          <option value="">Todos</option>
          <option v-for="a in apps" :key="a" :value="a">{{ a }}</option>
        </select>
      </div>

      <div class="col-md-1">
        <label>Conta</label>
        <input v-model.trim="accountId" class="form-control" placeholder="ID" @keypress.enter="fetchData()">
      </div>

      <div class="col-md-2">
        <label>De</label>
        <input type="date" v-model="startDate" class="form-control" @change="fetchData()">
      </div>

      <div class="col-md-2">
        <label>Até</label>
        <input type="date" v-model="endDate" class="form-control" @change="fetchData()">
      </div>

      <div class="col-auto">
        <button v-for="d in [7, 14, 30]" :key="d" class="btn btn-sm btn-outline-secondary mr-1"
          @click="applyPreset(d)">{{ d }}d</button>
      </div>

      <div v-if="view === 'previsto'" class="col-auto">
        <label class="checkbox-inline mb-0">
          <input type="checkbox" v-model="weighted"> Ponderar por veículos
        </label>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">Carregando...</div>
    <div v-else-if="error" class="alert alert-danger mt-4">{{ error }}</div>

    <template v-else>
      <div class="row mt-4">
        <div class="col-md-3" v-for="card in statCards" :key="card.label">
          <div class="card stat-card">
            <div class="stat-value">{{ card.value }}</div>
            <div class="stat-label">{{ card.label }}</div>
          </div>
        </div>
      </div>

      <div class="card mt-4 p-3">
        <h6>Dia da semana × horário</h6>
        <div class="heatmap-scroll">
          <div class="heatmap">
            <div class="heatmap-row">
              <div class="heatmap-label"></div>
              <div v-for="h in hours" :key="'h' + h" class="heatmap-hour">{{ h % 3 === 0 ? h + 'h' : '' }}</div>
            </div>
            <div v-for="(day, w) in wdays" :key="day" class="heatmap-row">
              <div class="heatmap-label">{{ day }}</div>
              <div v-for="h in hours" :key="day + h" class="heatmap-cell"
                :style="{ background: heatColor(aggregate.matrix[w][h]) }"
                :title="day + ' ' + h + 'h — ' + aggregate.matrix[w][h] + ' ' + unit"></div>
            </div>
          </div>
          <div class="heatmap-legend">
            <span>0</span>
            <div class="legend-bar"></div>
            <span>{{ aggregate.matrixMax }} {{ unit }} (máx. da célula)</span>
          </div>
        </div>
      </div>

      <div class="card mt-4 p-3">
        <h6>Volume por dia</h6>
        <div class="bars-scroll">
          <div class="bars">
            <div v-for="[date, value] in aggregate.dates" :key="date" class="bar-col"
              :title="labelOf(date) + ' — ' + value + ' ' + unit">
              <span v-if="isPeak(date)" class="bar-peak-label">{{ value }}</span>
              <div class="bar" :style="{ height: barHeight(value) + 'px', opacity: isPeak(date) ? 1 : 0.72 }"></div>
              <span class="bar-label" :class="{ 'text-muted': isWeekend(date) }">{{ labelOf(date) }}</span>
            </div>
            <p v-if="!aggregate.dates.length" class="text-muted mb-0">Sem dados no período selecionado.</p>
          </div>
        </div>
      </div>

      <div class="card mt-4 p-3">
        <h6>Por conta {{ others && others.accounts > 0 ? '(top 50)' : '' }}</h6>
        <div class="table-responsive">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Conta</th>
                <th class="text-right" title="Total no período (uma placa pode gerar mais de uma consulta se houver reprocessos)">{{ view === 'realizado' ? 'Consultas' : 'Rodadas' }}</th>
                <th v-if="view === 'realizado'" class="text-right" title="Placas distintas consultadas no período — o tamanho real do trabalho">Placas</th>
                <th v-if="view === 'realizado'" class="text-right" title="Percentual da rodada já concluído (resultado final, sucesso ou erro); o restante está na fila ou processando">% concluído</th>
                <th v-if="view === 'previsto'" class="text-right" title="Veículos habilitados da conta (com filtro de spider, só os com aquele serviço ativo)">Veículos ativos</th>
                <th v-if="view === 'previsto'" class="text-right" title="Rodadas × veículos ativos: quantas consultas essas rodadas vão disparar">Consultas agendadas</th>
                <th title="Fila do SNE em que as próximas consultas da conta entram">Fila</th>
                <th v-if="view === 'realizado'" title="Situação de cada consulta do período">Status</th>
                <th class="text-right" title="Quanto a conta representa do volume total do período filtrado">Participação</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="view === 'realizado'">
                <tr v-for="acc in accounts" :key="acc.account_id">
                  <td><strong>{{ accountLabel(acc) }}</strong> <small class="text-muted">#{{ acc.account_id }}</small></td>
                  <td class="text-right">{{ acc.total }}</td>
                  <td class="text-right">{{ acc.plates }}</td>
                  <td class="text-right"
                    :title="conclusionOf(acc).done + ' de ' + acc.total + ' concluídas; ' + conclusionOf(acc).pending + ' pendentes (na fila ou processando)'">
                    {{ conclusionOf(acc).pct }}%
                  </td>
                  <td><span class="lane-badge" :title="laneTitle(acc.account_id)">{{ laneLabel(acc.account_id) }}</span></td>
                  <td>
                    <span v-for="entry in acc.statuses" :key="entry.status" class="status-chip">
                      <span class="status-dot" :style="{ background: statusColor(entry.status) }"></span>
                      {{ statusLabel(entry.status) }}: {{ entry.count }}
                    </span>
                  </td>
                  <td class="text-right">{{ percentOf(acc.total) }}</td>
                </tr>
              </template>
              <template v-else>
                <tr v-for="acc in accounts" :key="acc.account_id">
                  <td><strong>{{ acc.account_name || ('Conta ' + acc.account_id) }}</strong>
                    <small class="text-muted">#{{ acc.account_id }}</small></td>
                  <td class="text-right">{{ acc.events }}</td>
                  <td class="text-right">{{ acc.vehicles }}</td>
                  <td class="text-right">{{ acc.events * Math.max(acc.vehicles, 1) }}</td>
                  <td><span class="lane-badge" :title="laneTitle(acc.account_id)">{{ laneLabel(acc.account_id) }}</span></td>
                  <td class="text-right">{{ percentOf(acc.events) }}</td>
                </tr>
              </template>
              <tr v-if="others && others.accounts > 0">
                <td class="text-muted">Outras {{ others.accounts }} contas</td>
                <td class="text-right text-muted">{{ others.total }}</td>
                <td :colspan="view === 'previsto' ? 3 : 4"></td>
                <td class="text-right text-muted">{{ percentOf(others.total) }}</td>
              </tr>
              <tr v-if="!accounts.length">
                <td colspan="8" class="text-center text-muted py-4">Sem dados no período selecionado.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
const WDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const SNE_SCRAPER = 'autos_sne_denatran'
const STATUS_META = {
  processed: { label: 'Processadas', color: '#10b981' },
  processing: { label: 'Processando', color: '#0ea5e9' },
  unprocessed: { label: 'Na fila', color: '#94a3b8' },
  processed_with_user_error: { label: 'Erro do usuário', color: '#f59e0b' },
  processed_with_system_error: { label: 'Erro de sistema', color: '#f97316' },
  error: { label: 'Erro', color: '#ef4444' }
}

function toISO(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function shiftDays(days) {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return toISO(d)
}

function wdayOf(date) {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(y, m - 1, d).getDay()
}

export default {
  name: 'AdminVolumetria',
  data() {
    return {
      view: 'realizado',
      spider: '',
      app: '',
      accountId: '',
      startDate: shiftDays(-13),
      endDate: shiftDays(0),
      weighted: true,
      loading: false,
      error: null,
      payload: null,
      services: [],
      accountNames: {},
      lanes: {},
      wdays: WDAYS,
      hours: Array.from({ length: 24 }, (_, i) => i),
      apps: ['cm-frotas', 'cm-crono', 'cm-taxas', 'cm-embarcador'],
      Math: Math
    }
  },
  created() {
    this.loadServices()
    this.loadAccounts()
    this.loadLanes()
    this.fetchData()
  },
  computed: {
    unit() {
      if (this.view === 'realizado') return 'consultas'
      return this.weighted ? 'consultas agendadas' : 'rodadas'
    },
    sneNames() {
      return this.services.filter(s => s.scraper_name === SNE_SCRAPER).map(s => s.name)
    },
    accounts() {
      return (this.payload && this.payload.accounts) || []
    },
    others() {
      return this.payload && this.payload.others
    },
    grandTotal() {
      return (this.payload && this.payload.total) || 0
    },
    aggregate() {
      const useWeighted = this.view === 'previsto' && this.weighted
      const matrix = Array.from({ length: 7 }, () => Array(24).fill(0))
      const byHour = Array(24).fill(0)
      let total = 0

      for (const cell of (this.payload && this.payload.matrix) || []) {
        const value = this.cellValue(cell, useWeighted)
        matrix[cell.wday][cell.hour] += value
        byHour[cell.hour] += value
        total += value
      }

      const dates = ((this.payload && this.payload.by_date) || [])
        .map(day => [day.date, this.cellValue(day, useWeighted)])
      const matrixMax = Math.max(0, ...matrix.map(row => Math.max(...row)))
      const dateMax = Math.max(0, ...dates.map(([, v]) => v))
      const peakDate = dates.reduce((best, cur) => (best && best[1] >= cur[1] ? best : cur), null)
      let peakHour = { hour: 0, value: 0 }
      byHour.forEach((value, hour) => { if (value > peakHour.value) peakHour = { hour, value } })
      const activeDays = dates.filter(([, v]) => v > 0).length

      return { matrix, matrixMax, dates, dateMax, peakDate, peakHour, activeDays, total }
    },
    statCards() {
      const agg = this.aggregate
      return [
        { label: `Total de ${this.unit}`, value: agg.total },
        { label: 'Média por dia ativo', value: agg.activeDays > 0 ? Math.round(agg.total / agg.activeDays) : 0 },
        {
          label: 'Dia de pico',
          value: agg.peakDate ? `${this.labelOf(agg.peakDate[0])} (${agg.peakDate[1]})` : '—'
        },
        {
          label: 'Horário de pico',
          value: agg.peakHour.value > 0 ? `${agg.peakHour.hour}h (${agg.peakHour.value})` : '—'
        }
      ]
    }
  },
  methods: {
    cellValue(cell, useWeighted) {
      if (cell.count != null) return cell.count
      return useWeighted ? (cell.weighted != null ? cell.weighted : cell.events || 0) : (cell.events || 0)
    },
    setView(view) {
      if (this.view === view) return
      this.view = view
      if (view === 'realizado') {
        this.startDate = shiftDays(-13)
        this.endDate = shiftDays(0)
      } else {
        this.startDate = shiftDays(0)
        this.endDate = shiftDays(30)
      }
      this.fetchData()
    },
    applyPreset(days) {
      if (this.view === 'realizado') {
        this.startDate = shiftDays(-(days - 1))
        this.endDate = shiftDays(0)
      } else {
        this.startDate = shiftDays(0)
        this.endDate = shiftDays(days)
      }
      this.fetchData()
    },
    spiderNames() {
      if (this.spider === '__sne__') return this.sneNames
      return this.spider ? [this.spider] : []
    },
    buildQuery(serviceKey) {
      const q = { start_date: this.startDate, end_date: this.endDate }
      const names = this.spiderNames()
      if (names.length) q[serviceKey] = names
      if (this.app) q.app_name = this.app
      if (this.accountId) q.account_id = this.accountId
      return q
    },
    fetchData() {
      this.loading = true
      this.error = null
      const request = this.view === 'realizado'
        ? this.axios.get('admin/maestro/consults/distribution', { params: { q: this.buildQuery('scraping_service_name') } })
        : this.axios.get('admin/event_schedules/distribution', { params: { q: this.buildQuery('service_name') } })

      request
        .then(response => { this.payload = response.data || null })
        .catch(e => {
          this.error = (e && e.errors && e.errors[0]) || 'Erro ao carregar a volumetria. Tente novamente.'
        })
        .finally(() => { this.loading = false })
    },
    loadServices() {
      this.axios.get('admin/maestro/scraping_services', { params: { per_page: 300 } })
        .then(response => {
          const seen = new Set()
          this.services = (response.data || [])
            .filter(s => s.name && !seen.has(s.name) && seen.add(s.name))
            .sort((a, b) => (a.friendly_name || a.name).localeCompare(b.friendly_name || b.name))
        })
        .catch(() => {})
    },
    loadLanes() {
      this.axios.get('admin/accounts/sne_lanes')
        .then(response => {
          const map = {}
          for (const row of response.data || []) map[String(row.account_id)] = row
          this.lanes = map
        })
        .catch(() => {})
    },
    laneLabel(key) {
      const info = this.lanes[String(key)]
      const labels = { express: 'Express', pesada: 'Pesada', padrao: 'Padrão' }
      return labels[(info && info.lane) || 'padrao'] || 'Padrão'
    },
    laneTitle(key) {
      const info = this.lanes[String(key)]
      const sources = {
        manual: 'definida manualmente na conta',
        onboarding: 'automática: conta em onboarding',
        automatica: 'automática: pelo nº de veículos',
        padrao: 'sem configuração: fila atual'
      }
      return 'Fila do SNE desta conta (' + (sources[(info && info.source) || 'padrao'] || '') + ')'
    },
    conclusionOf(acc) {
      let pending = 0
      for (const entry of acc.statuses || []) {
        if (entry.status === 'unprocessed' || entry.status === 'processing') pending += entry.count
      }
      const done = acc.total - pending
      const pct = acc.total > 0 ? Math.round((done / acc.total) * 100) : 0
      return { done, pending, pct }
    },
    loadAccounts() {
      this.axios.get('admin/accounts', { params: { per_page: 1000 } })
        .then(response => {
          const map = {}
          for (const account of response.data || []) {
            if (account.id != null && account.name) map[String(account.id)] = account.name
          }
          this.accountNames = map
        })
        .catch(() => {})
    },
    accountLabel(acc) {
      return this.accountNames[String(acc.account_id)] || `Conta ${acc.account_id}`
    },
    percentOf(value) {
      return this.grandTotal > 0 ? `${((value / this.grandTotal) * 100).toFixed(1)}%` : '—'
    },
    heatColor(value) {
      const max = this.aggregate.matrixMax
      if (value <= 0 || max <= 0) return 'transparent'
      const alpha = 0.08 + 0.87 * (value / max)
      return `rgba(37, 99, 235, ${alpha.toFixed(3)})`
    },
    barHeight(value) {
      const max = this.aggregate.dateMax
      return max > 0 ? Math.max(2, (value / max) * 128) : 2
    },
    isPeak(date) {
      return this.aggregate.peakDate && this.aggregate.peakDate[0] === date
    },
    isWeekend(date) {
      const w = wdayOf(date)
      return w === 0 || w === 6
    },
    labelOf(date) {
      const [, m, d] = date.split('-')
      return `${d}/${m}`
    },
    statusLabel(status) {
      return (STATUS_META[status] || { label: status }).label
    },
    statusColor(status) {
      return (STATUS_META[status] || { color: '#cbd5e1' }).color
    }
  }
}
</script>

<style scoped>
.stat-card {
  padding: 14px 16px;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
}

.stat-label {
  font-size: 12px;
  color: #6c757d;
}

.heatmap-scroll,
.bars-scroll {
  overflow-x: auto;
}

.heatmap {
  min-width: 720px;
}

.heatmap-row {
  display: flex;
  gap: 2px;
  margin-bottom: 2px;
}

.heatmap-label {
  width: 44px;
  font-size: 12px;
  color: #6c757d;
  text-align: right;
  padding-right: 8px;
  line-height: 24px;
  flex-shrink: 0;
}

.heatmap-hour {
  flex: 1;
  font-size: 10px;
  color: #6c757d;
  text-align: center;
  min-width: 24px;
}

.heatmap-cell {
  flex: 1;
  height: 24px;
  min-width: 24px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.heatmap-legend {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 11px;
  color: #6c757d;
}

.legend-bar {
  height: 8px;
  width: 128px;
  border-radius: 999px;
  background: linear-gradient(to right, rgba(37, 99, 235, 0.06), rgba(37, 99, 235, 0.95));
}

.bars {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 170px;
}

.bar-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 30px;
  flex-shrink: 0;
}

.bar {
  width: 100%;
  border-radius: 4px 4px 0 0;
  background: #2563eb;
}

.bar-peak-label {
  font-size: 10px;
  font-weight: 600;
}

.bar-label {
  font-size: 9px;
  color: #6c757d;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  margin-right: 10px;
  white-space: nowrap;
}

.lane-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  background: #e2e8f0;
  color: #334155;
  cursor: help;
}

.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
</style>
